import { Link } from 'react-router-dom';
import { Field } from './ui';
import type {
  NewsletterBlockContent,
  NewsletterBlockType,
  NewsletterDate,
  NewsletterImage,
} from '../types';

/**
 * The editors for one newsletter block each.
 *
 * Every block is permanently open rather than something you click to expand,
 * and moving one is two arrow buttons rather than a drag. Both are deliberate.
 * The committee are volunteers, mostly not confident online, and drag-and-drop
 * is the part of every email builder people give up on — it needs a steady
 * hand, it does not work on a phone, and there is nothing to press if it goes
 * wrong.
 *
 * Nothing here formats anything. A block holds plain text and the server turns
 * it into email HTML, so what a volunteer types can never become markup.
 *
 * Every field id is built from the block's uid, because a newsletter usually
 * has several paragraphs and "What it says" would otherwise be the id of all
 * of them — which quietly breaks every label on the page but the first.
 */

interface BlockKind {
  type: NewsletterBlockType;
  label: string;
  hint: string;
  blank: NewsletterBlockContent;
}

/** What the "add" row offers, in the order it offers it. */
export const BLOCK_KINDS: BlockKind[] = [
  {
    type: 'heading',
    label: 'Heading',
    hint: 'A line that starts a new section.',
    blank: { text: '' },
  },
  {
    type: 'text',
    label: 'Paragraph',
    hint: 'Ordinary writing. Most of a newsletter is this.',
    blank: { text: '' },
  },
  {
    type: 'image',
    label: 'Picture',
    hint: 'Across the full width, with a caption underneath.',
    blank: { image_id: 0, url: '', alt: '', caption: '' },
  },
  {
    type: 'image_text',
    label: 'Picture beside text',
    hint: 'A smaller picture on the left with writing next to it.',
    blank: { image_id: 0, url: '', alt: '', text: '' },
  },
  {
    type: 'columns',
    label: 'Two columns',
    hint: 'Two short pieces side by side.',
    blank: { left_heading: '', left_text: '', right_heading: '', right_text: '' },
  },
  {
    type: 'dates',
    label: 'Dates for the diary',
    hint: 'A list of what is on and where.',
    blank: { items: [{ when: '', what: '', where: '' }] },
  },
  {
    type: 'button',
    label: 'Button',
    hint: 'A link that looks like a button, for one thing you want pressed.',
    blank: { label: '', url: '' },
  },
  {
    type: 'divider',
    label: 'Dividing line',
    hint: 'A thin rule, to separate two sections.',
    blank: {},
  },
];

const KIND_LABELS: Record<NewsletterBlockType, string> = {
  header: 'Top of the newsletter',
  heading: 'Heading',
  text: 'Paragraph',
  image: 'Picture',
  image_text: 'Picture beside text',
  columns: 'Two columns',
  button: 'Button',
  dates: 'Dates for the diary',
  divider: 'Dividing line',
  footer: 'Bottom of the newsletter',
};

export function blockLabel(type: NewsletterBlockType): string {
  return KIND_LABELS[type] ?? type;
}

/** Neither of these is the volunteer's to move or remove. */
export function isFixed(type: NewsletterBlockType): boolean {
  return type === 'header' || type === 'footer';
}

interface EditorProps {
  /** Unique to this block on this screen, so the field ids are too. */
  uid: string;
  type: NewsletterBlockType;
  content: NewsletterBlockContent;
  images: NewsletterImage[];
  onChange: (content: NewsletterBlockContent) => void;
}

export function BlockEditor({ uid, type, content, images, onChange }: EditorProps) {
  // One setter for every field, so each editor below is only its fields.
  const set = (patch: Partial<NewsletterBlockContent>) => onChange({ ...content, ...patch });

  switch (type) {
    case 'header':
      return (
        <>
          <Field
            id={`${uid}-issue`}
            label="Which issue this is"
            hint="Shown under the association's name. Leave it empty if you would rather not say."
            value={content.issue ?? ''}
            onChange={(e) => set({ issue: e.target.value })}
            placeholder="September 2026"
          />
          <p className="hint">
            The picture at the top is the one chosen on the{' '}
            <Link to="/email/newsletter-images">pictures page</Link>, so every newsletter uses the
            same one. Change it there and it changes here.
          </p>
        </>
      );

    case 'heading':
      return (
        <Field
          id={`${uid}-text`}
          label="What it says"
          value={content.text ?? ''}
          onChange={(e) => set({ text: e.target.value })}
          placeholder="Asian hornet watch"
        />
      );

    case 'text':
      return (
        <Area
          id={`${uid}-text`}
          label="What it says"
          value={content.text ?? ''}
          rows={6}
          onChange={(value) => set({ text: value })}
        />
      );

    case 'image':
      return (
        <>
          <PicturePicker uid={uid} images={images} content={content} onPick={set} />
          <Field
            id={`${uid}-alt`}
            label="Describe the picture"
            hint="Most email programs hide pictures until asked. This is what shows instead, so it is worth writing."
            value={content.alt ?? ''}
            onChange={(e) => set({ alt: e.target.value })}
            placeholder="Two beekeepers inspecting a hive"
          />
          <Field
            id={`${uid}-caption`}
            label="Caption"
            hint="Printed under the picture, in italics. Optional."
            value={content.caption ?? ''}
            onChange={(e) => set({ caption: e.target.value })}
          />
        </>
      );

    case 'image_text':
      return (
        <>
          <PicturePicker uid={uid} images={images} content={content} onPick={set} />
          <Field
            id={`${uid}-alt`}
            label="Describe the picture"
            hint="Shown when a program hides pictures, which many do by default."
            value={content.alt ?? ''}
            onChange={(e) => set({ alt: e.target.value })}
          />
          <Area
            id={`${uid}-text`}
            label="The writing beside it"
            value={content.text ?? ''}
            rows={5}
            onChange={(value) => set({ text: value })}
          />
        </>
      );

    case 'columns':
      return (
        <div className="two-up">
          <div className="stack">
            <Field
              id={`${uid}-lh`}
              label="Left heading"
              value={content.left_heading ?? ''}
              onChange={(e) => set({ left_heading: e.target.value })}
            />
            <Area
              id={`${uid}-lt`}
              label="Left column"
              value={content.left_text ?? ''}
              rows={5}
              onChange={(value) => set({ left_text: value })}
            />
          </div>
          <div className="stack">
            <Field
              id={`${uid}-rh`}
              label="Right heading"
              value={content.right_heading ?? ''}
              onChange={(e) => set({ right_heading: e.target.value })}
            />
            <Area
              id={`${uid}-rt`}
              label="Right column"
              value={content.right_text ?? ''}
              rows={5}
              onChange={(value) => set({ right_text: value })}
            />
          </div>
        </div>
      );

    case 'button':
      return (
        <div className="two-up">
          <Field
            id={`${uid}-label`}
            label="What it says"
            value={content.label ?? ''}
            onChange={(e) => set({ label: e.target.value })}
            placeholder="Book a place"
          />
          <Field
            id={`${uid}-url`}
            label="Where it goes"
            hint="The whole address, starting with https://"
            value={content.url ?? ''}
            onChange={(e) => set({ url: e.target.value })}
            placeholder="https://..."
          />
        </div>
      );

    case 'dates':
      return (
        <DatesEditor
          uid={uid}
          items={content.items ?? []}
          onChange={(items) => set({ items })}
        />
      );

    case 'divider':
      return <p className="hint">Nothing to fill in — it draws a line.</p>;

    case 'footer':
      return (
        <p className="hint">
          Every newsletter ends with who sent it and how somebody can stop receiving them. It is
          not removable: a bulk email without those is how an association's address ends up
          blocked, and then nobody hears anything at all.
        </p>
      );

    default:
      return null;
  }
}

/** The repeating rows of a "dates for the diary" block. */
function DatesEditor({
  uid,
  items,
  onChange,
}: {
  uid: string;
  items: NewsletterDate[];
  onChange: (items: NewsletterDate[]) => void;
}) {
  const rows = items.length > 0 ? items : [{ when: '', what: '', where: '' }];

  const edit = (index: number, patch: Partial<NewsletterDate>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <>
      <div className="scroll-x">
        <table className="dates-editor">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">What</th>
              <th scope="col">Where</th>
              <th scope="col">
                <span className="visually-hidden">Remove the row</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${uid}-row-${index}`}>
                <td>
                  <input
                    aria-label={`When, row ${index + 1}`}
                    value={row.when}
                    placeholder="4 Oct"
                    onChange={(e) => edit(index, { when: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`What, row ${index + 1}`}
                    value={row.what}
                    placeholder="Honey show"
                    onChange={(e) => edit(index, { what: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Where, row ${index + 1}`}
                    value={row.where}
                    placeholder="Newport"
                    onChange={(e) => edit(index, { where: e.target.value })}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="link danger"
                    onClick={() => onChange(rows.filter((_, i) => i !== index))}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="actions">
        <button type="button" onClick={() => onChange([...rows, { when: '', what: '', where: '' }])}>
          Add a date
        </button>
      </div>
      <p className="hint">
        Write the date however you like — "4 Oct", or "Saturday 4 October". A row with nothing in
        the middle column is dropped when you save.
      </p>
    </>
  );
}

/**
 * Choosing a picture from the ones already uploaded.
 *
 * Thumbnails rather than a dropdown of filenames: the names are generated, so
 * a list of them tells nobody anything. Pasting an address is offered as well,
 * for a picture kept somewhere else, but second — an uploaded one is the one
 * that will still be there next year.
 */
function PicturePicker({
  uid,
  images,
  content,
  onPick,
}: {
  uid: string;
  images: NewsletterImage[];
  content: NewsletterBlockContent;
  onPick: (patch: Partial<NewsletterBlockContent>) => void;
}) {
  const chosen = content.image_id ?? 0;

  return (
    <div className="picture-picker">
      <span className="field-label">Which picture</span>
      {images.length === 0 ? (
        <p className="hint">
          No pictures have been uploaded yet.{' '}
          <Link to="/email/newsletter-images">Add some</Link>, or paste an address below.
        </p>
      ) : (
        <ul className="thumbs">
          {images.map((image) => (
            <li key={image.id}>
              <button
                type="button"
                className={chosen === image.id ? 'thumb on' : 'thumb'}
                aria-pressed={chosen === image.id}
                title={image.original_name ?? image.filename}
                onClick={() =>
                  onPick(
                    chosen === image.id
                      ? { image_id: 0, url: '' }
                      : { image_id: image.id, url: image.url },
                  )
                }
              >
                <img src={image.url} alt="" width={image.width} height={image.height} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Field
        id={`${uid}-url`}
        label="Or paste a web address"
        hint="For a picture kept somewhere else. An uploaded one is safer — an address can stop working."
        value={chosen > 0 ? '' : content.url ?? ''}
        disabled={chosen > 0}
        onChange={(e) => onPick({ image_id: 0, url: e.target.value })}
        placeholder="https://..."
      />
    </div>
  );
}

/** A labelled textarea, which ui.tsx has no equivalent of. */
function Area({
  id,
  label,
  value,
  rows,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  rows: number;
  onChange: (value: string) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        aria-describedby={`${id}-hint`}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="hint" id={`${id}-hint`}>
        A blank line starts a new paragraph. <code>**bold**</code>, <code>*italic*</code>, and{' '}
        <code>[the words](https://the-address)</code> for a link. Type{' '}
        <code>{'{{first_name}}'}</code> to greet each member by name.
      </span>
    </div>
  );
}
